import React, { useEffect, useMemo, useRef } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/** Detect iOS Safari accurately */
const isIOSSafari = () => {
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS && isSafari;
};

/** ---------- Helpers (all platforms) ---------- */
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const isSameDay = (a, b) =>
  a && b && a.toDateString() === b.toDateString();

const roundToNextQuarter = (base = new Date()) => {
  const t = new Date(base);
  const mins = t.getMinutes();
  const next = Math.ceil(mins / 15) * 15;
  if (next === 60) {
    t.setHours(t.getHours() + 1);
    t.setMinutes(0);
  } else {
    t.setMinutes(next);
  }
  t.setSeconds(0, 0);
  return t;
};

const clampTimeForDate = (candidateTime, selectedDate) => {
  // If selected date is today, clamp time to now rounded up to next 15
  const now = new Date();
  if (selectedDate && isSameDay(selectedDate, now)) {
    const min = roundToNextQuarter(now);
    if (!candidateTime || candidateTime < min) return min;
  }
  // For future dates, allow any time (keep minutes rounded to 15)
  if (candidateTime) {
    const t = new Date(candidateTime);
    const r = new Date(t);
    const remainder = r.getMinutes() % 15;
    if (remainder !== 0) {
      r.setMinutes(r.getMinutes() + (15 - remainder));
      r.setSeconds(0, 0);
    }
    return r;
  }
  return candidateTime;
};

export default function SmartDateTimePicker({
  date,
  time,
  onDate,
  onTime,
  datePlaceholder = "Select Date",
  timePlaceholder = "Select Time",
}) {
  const onIOS = useMemo(isIOSSafari, []);
  const dateRef = useRef(null);
  const timeRef = useRef(null);

  /** ✅ Portal container for desktop popper */
  useEffect(() => {
    if (onIOS) return;
    const id = "ldc-datepicker-portal";
    if (!document.getElementById(id)) {
      const el = document.createElement("div");
      el.id = id;
      document.body.appendChild(el);
    }
  }, [onIOS]);

  // 🛠 Improved fix: prevent date/time picker from popping after Google dropdown closes
  const handlePreventWhenGoogleDropdown = (e) => {
    const googleDropdown = document.querySelector(".pac-container");
    const isDropdownVisible =
      googleDropdown && googleDropdown.offsetParent !== null;

    // If Google dropdown is open, block focus immediately
    if (isDropdownVisible) {
      e.preventDefault();
      e.stopPropagation();
      if (document.activeElement) document.activeElement.blur();
      return false;
    }

    // Add short delay to avoid re-triggering after dropdown closes
    setTimeout(() => {
      const stillVisible =
        googleDropdown && googleDropdown.offsetParent !== null;
      if (stillVisible && document.activeElement) {
        document.activeElement.blur();
      }
    }, 200);

    return true;
  };

  /** ---------- iOS Safari Picker (native inputs) ---------- **/
  if (onIOS) {
    const today = new Date();

    const formatLocalDate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;

    const minDateStr = formatLocalDate(today);

    return (
      <div className="safari-picker-pro">
        {/* 📅 DATE FIELD */}
        <div className="input-wrapper">
          <label className="picker-label">📅 Select Date</label>
          <input
            type="date"
            className="ios-picker-btn"
            min={minDateStr}
            value={date ? formatLocalDate(date) : ""}
            onChange={(e) => {
              const value = e.target.value;
              if (!value) {
                onDate(null);
                return;
              }
              const [y, m, d] = value.split("-").map(Number);
              const selected = new Date(y, m - 1, d);

              const todayStart = startOfDay(today);
              if (selected < todayStart) {
                e.target.value = minDateStr;
                onDate(todayStart);
                if (time) onTime(clampTimeForDate(time, todayStart));
                return;
              }

              onDate(selected);
              if (time) {
                const fixed = clampTimeForDate(time, selected);
                if (fixed && (!time || fixed.getTime() !== time.getTime())) {
                  onTime(fixed);
                }
              }
            }}
          />
        </div>

        {/* ⏰ TIME FIELD */}
        <div className="input-wrapper">
          <label className="picker-label">⏰ Select Time</label>
          <input
            type="time"
            className="ios-picker-btn"
            step="60"
            max="23:59"
            min={
              date && isSameDay(date, today)
                ? new Date().toTimeString().slice(0, 5)
                : "00:00"
            }
            value={
              time ? new Date(time).toTimeString().slice(0, 5) : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                onTime(null);
                return;
              }
              let [hh, mm] = v.split(":").map(Number);
              const rem = mm % 15;
              if (rem !== 0) {
                mm = mm + (15 - rem);
                if (mm >= 60) {
                  mm = 0;
                  hh = (hh + 1) % 24;
                }
              }
              const picked = new Date();
              picked.setHours(hh, mm, 0, 0);
              const fixed = clampTimeForDate(picked, date);
              const out = fixed || picked;
              const hhStr = String(out.getHours()).padStart(2, "0");
              const mmStr = String(out.getMinutes()).padStart(2, "0");
              if (e.target.value !== `${hhStr}:${mmStr}`) {
                e.target.value = `${hhStr}:${mmStr}`;
              }
              onTime(out);
            }}
          />
        </div>
      </div>
    );
  }

  /** ---------- Desktop & Android (React DatePicker) ---------- **/
  const today = new Date();
  const minDate = startOfDay(today);
  const nowRounded = roundToNextQuarter(today);
  const isTodaySelected = date && isSameDay(date, today);
  const dayAnchor = startOfDay(new Date());
  const minTimeObj = isTodaySelected ? nowRounded : new Date(dayAnchor.setHours(0, 0, 0, 0));
  const maxTimeObj = new Date(startOfDay(new Date()).setHours(23, 59, 59, 999));

  return (
    <div className="row-2">
      {/* 📅 Date Picker */}
      <ReactDatePicker
        ref={dateRef}
        onFocus={handlePreventWhenGoogleDropdown}
        selected={date}
        onChange={(newDate) => {
          if (!newDate) {
            onDate(null);
            return;
          }
          const chosen = startOfDay(newDate);
          const safe = chosen < minDate ? minDate : chosen;
          onDate(safe);
          if (time) {
            const fixed = clampTimeForDate(time, safe);
            if (fixed && fixed.getTime() !== time.getTime()) {
              onTime(fixed);
            }
          }
        }}
        placeholderText={datePlaceholder}
        dateFormat="dd/MM/yyyy"
        minDate={minDate}
        className="custom-input"
        withPortal
        portalId="ldc-datepicker-portal"
        popperPlacement="bottom-start"
        popperProps={{ strategy: "fixed" }}
        popperModifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
        ]}
      />

      {/* ⏰ Time Picker */}
      <ReactDatePicker
        ref={timeRef}
        onFocus={handlePreventWhenGoogleDropdown}
        selected={time}
        onChange={(newTime) => {
          if (!newTime) {
            onTime(null);
            return;
          }
          const candidate = new Date(newTime);
          const fixed = clampTimeForDate(candidate, date);
          onTime(fixed || candidate);
        }}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={15}
        timeCaption="Time"
        dateFormat="h:mm aa"
        placeholderText={timePlaceholder}
        className="custom-input"
        minTime={minTimeObj}
        maxTime={maxTimeObj}
        filterTime={(t) => {
          if (!isTodaySelected) return true;
          const tt = new Date(t);
          tt.setSeconds(0, 0);
          return tt >= nowRounded;
        }}
        withPortal
        portalId="ldc-datepicker-portal"
        popperPlacement="bottom-start"
        popperProps={{ strategy: "fixed" }}
        popperModifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
        ]}
      />
    </div>
  );
}
